<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import { router } from '@inertiajs/vue3'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ReadinessAreaData = App.Data.Dashboard.ReadinessAreaData

export interface UploadSuggestion {
  attachableType: 'natural-person' | 'estate-object' | 'company'
  documentType: string
}

const props = defineProps<{
  area: ReadinessAreaData
  /** Fehlt das vorgeschlagene Dokument noch, steht hier eins – sonst null. */
  suggestion: UploadSuggestion | null
}>()

const emit = defineEmits<{
  answer: [applicable: boolean]
  upload: [suggestion: UploadSuggestion]
}>()

const { t } = useI18n()

const areaIcons: Record<string, string> = {
  profile: 'pi pi-id-card',
  credit: 'pi pi-chart-line',
  companies: 'pi pi-building',
  portfolio: 'pi pi-warehouse',
}

/** Ein optionaler Bereich ohne einen einzigen Datensatz hat noch nichts, was fehlen könnte. */
const isEmpty = computed(() => props.area.state === 'incomplete' && props.area.itemCount === 0)

const visibleFields = computed(() => props.area.missingFields.slice(0, 3))

const hiddenFieldCount = computed(() => props.area.missingCount - visibleFields.value.length)
</script>

<template>
  <div class="border-line bg-surface flex flex-col gap-3 rounded-xl border p-4">
    <div class="flex items-center gap-2.5">
      <span
        class="flex size-8 flex-none items-center justify-center rounded-lg"
        :class="area.state === 'not_applicable' ? 'bg-surface-3 text-ink-3' : 'bg-surface-3 text-ink-2'">
        <i :class="areaIcons[area.key]" class="text-sm!" aria-hidden="true" />
      </span>
      <p class="font-bold" :class="area.state === 'not_applicable' && 'text-ink-3'">{{ t(`areas.${area.key}`) }}</p>
    </div>

    <template v-if="area.state === 'unanswered'">
      <p class="text-ink text-sm font-medium">{{ t(`question.${area.key}`) }}</p>
      <div class="grid grid-cols-2 gap-2">
        <button
          type="button"
          class="border-line hover:border-action hover:text-action hover:bg-info-subtle flex cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-sm font-medium transition-colors"
          @click="emit('answer', true)">
          {{ t('answer.yes') }}
        </button>
        <button
          type="button"
          class="border-line hover:border-action hover:text-action hover:bg-info-subtle flex cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-sm font-medium transition-colors"
          @click="emit('answer', false)">
          {{ t(`answer.no.${area.key}`) }}
        </button>
      </div>
    </template>

    <template v-else-if="area.state === 'not_applicable'">
      <p class="text-ink-3 text-xs">{{ t(`notApplicable.${area.key}`) }}</p>
      <button
        type="button"
        class="text-action mt-auto flex cursor-pointer items-center gap-1.5 self-start text-sm font-medium"
        @click="emit('answer', true)">
        <i class="pi pi-replay text-xs!" aria-hidden="true" />
        {{ t(`notApplicable.undo.${area.key}`) }}
      </button>
    </template>

    <template v-else-if="area.state === 'complete'">
      <p class="text-success flex items-center gap-1.5 text-sm font-medium">
        <i class="pi pi-check text-xs!" aria-hidden="true" />
        {{ t('complete') }}
      </p>
    </template>

    <template v-else-if="isEmpty">
      <p class="text-ink-2 text-xs">{{ t(`empty.${area.key}`) }}</p>
    </template>

    <ul v-else class="flex flex-col gap-1.5">
      <li
        v-for="field in visibleFields"
        :key="field"
        class="grid grid-cols-[14px_minmax(0,1fr)] items-start gap-2 text-xs">
        <i
          class="border-warning bg-warning-subtle mt-0.5 size-3 rounded-[3px] border border-dashed"
          aria-hidden="true" />
        <span class="text-ink">{{ field }}</span>
      </li>
      <li v-if="hiddenFieldCount > 0" class="text-ink-3 pl-[22px] text-xs">
        {{ t('moreFields', hiddenFieldCount) }}
      </li>
    </ul>

    <button
      v-if="suggestion"
      type="button"
      class="border-info-subtle-active bg-info-subtle hover:border-action hover:bg-info-subtle-hover mt-auto flex w-full cursor-pointer items-center gap-2 rounded-[10px] border border-dashed p-2.5 text-left transition-colors"
      @click="emit('upload', suggestion)">
      <span class="bg-surface text-action flex size-8 flex-none items-center justify-center rounded-lg">
        <i class="pi pi-upload text-sm!" aria-hidden="true" />
      </span>
      <span class="text-ink text-xs font-bold">{{ t(`upload.${area.key}`) }}</span>
    </button>

    <div :class="suggestion ? 'pt-1' : 'mt-auto pt-1'">
      <FinestButton
        variant="ghost"
        size="sm"
        icon="pi pi-arrow-right"
        icon-pos="right"
        :label="isEmpty ? t(`action.create.${area.key}`) : t(`action.open.${area.key}`)"
        @click="router.visit(area.url)" />
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "areas": {
      "profile": "Profile",
      "credit": "Credit rating",
      "companies": "Companies",
      "portfolio": "Portfolio"
    },
    "complete": "All entries provided",
    "moreFields": "and {count} more entry | and {count} more entries",
    "empty": {
      "companies": "No company recorded yet.",
      "portfolio": "No property recorded yet."
    },
    "question": {
      "companies": "Do you hold real estate through a company?",
      "portfolio": "Do you already own real estate?"
    },
    "answer": {
      "yes": "Yes",
      "no": {
        "companies": "No",
        "portfolio": "Not yet"
      }
    },
    "notApplicable": {
      "companies": "You told us you do not hold real estate through a company.",
      "portfolio": "You told us you do not own real estate yet.",
      "undo": {
        "companies": "I do have a company",
        "portfolio": "I do own real estate"
      }
    },
    "upload": {
      "profile": "Upload identity card or passport",
      "credit": "Upload payslip",
      "companies": "Upload annual financial statement",
      "portfolio": "Upload loan contract"
    },
    "action": {
      "open": {
        "profile": "Open profile",
        "credit": "Open self-disclosure",
        "companies": "Open companies",
        "portfolio": "Open portfolio"
      },
      "create": {
        "companies": "Add company",
        "portfolio": "Add property"
      }
    }
  },
  "de": {
    "areas": {
      "profile": "Profil",
      "credit": "Bonität",
      "companies": "Unternehmen",
      "portfolio": "Portfolio"
    },
    "complete": "Alle Angaben vorhanden",
    "moreFields": "und {count} weitere Angabe | und {count} weitere Angaben",
    "empty": {
      "companies": "Noch kein Unternehmen erfasst.",
      "portfolio": "Noch kein Objekt erfasst."
    },
    "question": {
      "companies": "Hältst du Immobilien über ein Unternehmen?",
      "portfolio": "Besitzt du bereits Immobilien?"
    },
    "answer": {
      "yes": "Ja",
      "no": {
        "companies": "Nein",
        "portfolio": "Noch keine"
      }
    },
    "notApplicable": {
      "companies": "Du hältst keine Immobilien über ein Unternehmen.",
      "portfolio": "Du besitzt noch keine Immobilien.",
      "undo": {
        "companies": "Doch, ich habe ein Unternehmen",
        "portfolio": "Doch, ich habe Immobilien"
      }
    },
    "upload": {
      "profile": "Personalausweis oder Reisepass hochladen",
      "credit": "Gehaltsabrechnung hochladen",
      "companies": "Jahresabschluss hochladen",
      "portfolio": "Darlehensvertrag hochladen"
    },
    "action": {
      "open": {
        "profile": "Profil öffnen",
        "credit": "Selbstauskunft öffnen",
        "companies": "Unternehmen öffnen",
        "portfolio": "Portfolio öffnen"
      },
      "create": {
        "companies": "Unternehmen anlegen",
        "portfolio": "Objekt anlegen"
      }
    }
  }
}
</i18n>
